import {
  Badge,
  Box,
  Button,
  Circle,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Skeleton,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import Tooltip from '../../../components/Tooltip';
import { AnimatePresence, motion } from 'framer-motion';
import { endOfDay, format, formatDistanceToNow, isToday, isYesterday, startOfDay, subDays } from 'date-fns';
import esLocale from 'date-fns/locale/es';
import { useMemo, useState } from 'react';
import {
  FiArrowRight,
  FiCheckCircle,
  FiEdit3,
  FiGlobe,
  FiInbox,
  FiMonitor,
  FiPlusCircle,
  FiSearch,
  FiUser,
  FiXCircle,
} from 'react-icons/fi';
import {
  EventoReserva,
  TipoEvento,
  momentoDe,
  nombreDeCampo,
  valorLegible,
  dondeOcurrioCorto,
} from '~/core/tracking/reservationEvents';
import { filtrarEventos, useActividadEmpresa } from '~/hooks/useReservationEvents';

const MotionBox = motion(Box);

const ASPECTO: Record<TipoEvento, { icono: typeof FiPlusCircle; color: string; nombre: string }> = {
  created: { icono: FiPlusCircle, color: 'green', nombre: 'Creadas' },
  modified: { icono: FiEdit3, color: 'orange', nombre: 'Modificadas' },
  cancelled: { icono: FiXCircle, color: 'red', nombre: 'Canceladas' },
  status: { icono: FiCheckCircle, color: 'blue', nombre: 'Cambios de estado' },
};

const RANGOS = [
  { clave: 'hoy', etiqueta: 'Hoy', dias: 0 },
  { clave: '7', etiqueta: '7 días', dias: 7 },
  { clave: '30', etiqueta: '30 días', dias: 30 },
  { clave: 'todo', etiqueta: 'Todo', dias: -1 },
];

/** Encabezado de cada grupo: Hoy, Ayer o la fecha. */
const tituloDelDia = (ms: number) => {
  const fecha = new Date(ms);
  if (isToday(fecha)) return 'Hoy';
  if (isYesterday(fecha)) return 'Ayer';
  return format(fecha, "EEEE d 'de' MMMM", { locale: esLocale });
};

const Fila = ({ evento, indice }: { evento: EventoReserva; indice: number }) => {
  const aspecto = ASPECTO[evento.type] ?? ASPECTO.status;
  const ms = momentoDe(evento);
  const cuando = ms ? formatDistanceToNow(new Date(ms), { locale: esLocale, addSuffix: true }) : '';
  const exacta = ms ? format(new Date(ms), "d MMM yyyy, h:mm a", { locale: esLocale }) : '';
  const esCliente = evento.actor?.kind === 'customer';

  return (
    <MotionBox
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25, delay: Math.min(indice * 0.025, 0.3) }}
      borderWidth='1px'
      borderColor='gray.100'
      borderRadius='lg'
      bg='white'
      px={4}
      py={3}
      _hover={{ borderColor: `${aspecto.color}.200`, shadow: 'sm' }}
    >
      <Flex gap={3} align='flex-start'>
        <Circle size='34px' bg={`${aspecto.color}.50`} flexShrink={0}>
          <Icon as={aspecto.icono} color={`${aspecto.color}.500`} boxSize={4} />
        </Circle>

        <Box flex={1} minW={0}>
          <HStack spacing={2} flexWrap='wrap'>
            <Text fontSize='sm' fontWeight='medium' color='gray.800'>
              {evento.summary}
            </Text>
            {evento.code && (
              <Badge colorScheme='blue' variant='subtle' fontSize='0.65rem'>
                {evento.code}
              </Badge>
            )}
          </HStack>

          <HStack spacing={3} mt={1} flexWrap='wrap' color='gray.500'>
            <HStack spacing={1}>
              <Icon as={FiUser} boxSize={3.5} color={esCliente ? 'gray.400' : 'blue.400'} />
              <Text fontSize='xs'>
                {evento.actor?.kind === 'unknown'
                  ? 'Sin registrar'
                  : esCliente
                  ? 'El cliente'
                  : evento.actor?.name || evento.actor?.email || 'Equipo'}
              </Text>
            </HStack>
            <HStack spacing={1}>
              <Icon
                as={evento.origin?.channel === 'dashboard' ? FiMonitor : FiGlobe}
                boxSize={3.5}
              />
              <Text fontSize='xs'>{dondeOcurrioCorto(evento.origin)}</Text>
            </HStack>
            <Text fontSize='xs' color='gray.400'>
              {evento.customer?.name}
            </Text>
            <Text fontSize='xs' color='gray.400'>
              · {evento.locationName}
            </Text>
          </HStack>

          {evento.changes?.length > 0 && (
            <Wrap spacing={2} mt={2}>
              {evento.changes.map((c) => (
                <WrapItem key={c.field}>
                  <HStack spacing={1.5} fontSize='xs'>
                    <Text color='gray.500'>{nombreDeCampo(c.field)}</Text>
                    <Badge colorScheme='gray' variant='subtle' fontWeight='normal'>
                      {valorLegible(c.field, c.before)}
                    </Badge>
                    <Icon as={FiArrowRight} color='gray.400' boxSize={3} />
                    <Badge colorScheme='green' variant='subtle' fontWeight='normal'>
                      {valorLegible(c.field, c.after)}
                    </Badge>
                  </HStack>
                </WrapItem>
              ))}
            </Wrap>
          )}
        </Box>

        <Tooltip label={exacta} placement='left' hasArrow openDelay={300}>
          <Text fontSize='xs' color='gray.400' flexShrink={0} whiteSpace='nowrap'>
            {cuando}
          </Text>
        </Tooltip>
      </Flex>
    </MotionBox>
  );
};

type Props = {
  /** Empresa cuya actividad se muestra. */
  companyId?: string;
  /** Se oculta el título cuando va dentro de otra pantalla con pestañas. */
  sinTitulo?: boolean;
};

/**
 * Actividad de las reservas en tiempo real: quién hizo qué y desde dónde.
 *
 * Se alimenta de `reservation_events`, que guarda un evento por acción con el
 * autor y el origen siempre presentes. La pantalla de registros del superadmin
 * sigue existiendo tal cual; esta es la que puede usar el cliente.
 */
const PanelActividad = ({ companyId, sinTitulo }: Props) => {
  const { eventos, cargando, error } = useActividadEmpresa(companyId);

  const [busqueda, setBusqueda] = useState('');
  const [tipo, setTipo] = useState<string>('todos');
  const [locationId, setLocationId] = useState('');
  const [rango, setRango] = useState('7');

  const sedes = useMemo(() => {
    const mapa = new Map<string, string>();
    eventos.forEach((e) => {
      if (e.locationId) mapa.set(e.locationId, e.locationName);
    });
    return [...mapa.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [eventos]);

  const filtrados = useMemo(() => {
    const hoy = new Date();
    const def = RANGOS.find((r) => r.clave === rango);
    const desde =
      !def || def.dias < 0 ? null : def.dias === 0 ? startOfDay(hoy) : startOfDay(subDays(hoy, def.dias));
    return filtrarEventos(eventos, {
      tipo,
      locationId,
      desde,
      hasta: endOfDay(hoy),
      busqueda,
    });
  }, [eventos, tipo, locationId, rango, busqueda]);

  const conteos = useMemo(() => {
    const c: Record<string, number> = { created: 0, modified: 0, cancelled: 0, status: 0 };
    filtrados.forEach((e) => (c[e.type] = (c[e.type] ?? 0) + 1));
    return c;
  }, [filtrados]);

  // Los eventos vienen ordenados del más reciente al más antiguo.
  const porDia = useMemo(() => {
    const grupos: Array<{ titulo: string; eventos: EventoReserva[] }> = [];
    filtrados.forEach((e) => {
      const titulo = tituloDelDia(momentoDe(e));
      const ultimo = grupos[grupos.length - 1];
      if (ultimo && ultimo.titulo === titulo) ultimo.eventos.push(e);
      else grupos.push({ titulo, eventos: [e] });
    });
    return grupos;
  }, [filtrados]);

  return (
    <Box px={{ base: 3, md: 6 }} py={5} maxW='1100px' mx='auto'>
      {!sinTitulo && (
        <HStack mb={1} spacing={3}>
          <Text fontSize='xl' fontWeight='bold'>
            Actividad
          </Text>
          <HStack spacing={1.5} color='green.500'>
            <MotionBox
              w='7px'
              h='7px'
              borderRadius='full'
              bg='green.400'
              animate={{ opacity: [1, 0.25, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <Text fontSize='xs'>en vivo</Text>
          </HStack>
        </HStack>
      )}
      <Text fontSize='sm' color='gray.500' mb={5}>
        Quién creó, modificó o canceló cada reserva, y desde dónde lo hizo.
      </Text>

      {/* Resumen */}
      <Wrap spacing={4} mb={5}>
        {(Object.keys(ASPECTO) as TipoEvento[]).map((t) => (
          <WrapItem key={t}>
            <Stat
              minW='120px'
              px={4}
              py={2}
              borderWidth='1px'
              borderColor={tipo === t ? `${ASPECTO[t].color}.300` : 'gray.100'}
              bg={tipo === t ? `${ASPECTO[t].color}.50` : 'white'}
              borderRadius='lg'
              cursor='pointer'
              transition='all .15s'
              _hover={{ borderColor: `${ASPECTO[t].color}.200` }}
              onClick={() => setTipo(tipo === t ? 'todos' : t)}
            >
              <StatLabel fontSize='xs' color='gray.500'>
                {ASPECTO[t].nombre}
              </StatLabel>
              <StatNumber fontSize='xl' color={`${ASPECTO[t].color}.600`}>
                {conteos[t] ?? 0}
              </StatNumber>
            </Stat>
          </WrapItem>
        ))}
      </Wrap>

      {/* Filtros */}
      <Flex gap={3} mb={5} flexWrap='wrap' align='center'>
        <InputGroup size='sm' maxW='340px' flex='1 1 260px'>
          <InputLeftElement pointerEvents='none'>
            <Icon as={FiSearch} color='gray.400' />
          </InputLeftElement>
          <Input
            placeholder='Buscar código, cliente o persona'
            borderRadius='md'
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </InputGroup>

        <Select
          size='sm'
          maxW='190px'
          borderRadius='md'
          value={locationId}
          onChange={(e) => setLocationId(e.target.value)}
        >
          <option value=''>Todas las sedes</option>
          {sedes.map(([id, nombre]) => (
            <option key={id} value={id}>
              {nombre}
            </option>
          ))}
        </Select>

        <HStack spacing={1}>
          {RANGOS.map((r) => (
            <Button
              key={r.clave}
              size='xs'
              variant={rango === r.clave ? 'solid' : 'ghost'}
              colorScheme={rango === r.clave ? 'blue' : 'gray'}
              onClick={() => setRango(r.clave)}
            >
              {r.etiqueta}
            </Button>
          ))}
        </HStack>

        {(tipo !== 'todos' || locationId || busqueda) && (
          <Button
            size='xs'
            variant='link'
            colorScheme='gray'
            onClick={() => {
              setTipo('todos');
              setLocationId('');
              setBusqueda('');
            }}
          >
            Limpiar filtros
          </Button>
        )}
      </Flex>

      {error && (
        <Box bg='red.50' borderWidth='1px' borderColor='red.200' borderRadius='md' p={3} mb={4}>
          <Text fontSize='sm' color='red.700'>
            No se pudo cargar la actividad: {error}
          </Text>
        </Box>
      )}

      {cargando && (
        <VStack align='stretch' spacing={3}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} h='72px' borderRadius='lg' />
          ))}
        </VStack>
      )}

      {!cargando && filtrados.length === 0 && (
        <VStack py={16} spacing={2} color='gray.500'>
          <Icon as={FiInbox} boxSize={8} color='gray.300' />
          <Text fontSize='sm'>
            {eventos.length === 0
              ? 'Todavía no hay actividad registrada.'
              : 'Ninguna actividad coincide con estos filtros.'}
          </Text>
        </VStack>
      )}

      {!cargando &&
        porDia.map((grupo) => (
          <Box key={grupo.titulo} mb={6}>
            <Text
              fontSize='xs'
              fontWeight='semibold'
              color='gray.400'
              textTransform='uppercase'
              letterSpacing='wide'
              mb={2}
            >
              {grupo.titulo}
            </Text>
            <VStack align='stretch' spacing={2}>
              <AnimatePresence initial={false}>
                {grupo.eventos.map((e, i) => (
                  <Fila key={e.id ?? `${e.reservationId}-${i}`} evento={e} indice={i} />
                ))}
              </AnimatePresence>
            </VStack>
          </Box>
        ))}
    </Box>
  );
};

export default PanelActividad;
